import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import {
  readStoredExternalChannelCampaign,
  readStoredExternalChannelOrigin,
} from '../utils/external-channel-access.util';

import { ApiService } from './api.service';
import { UtmPreservationService } from './utm-preservation.service';

export type EventOriginType = 'ACCESS_CHANNEL' | 'SALES_CHANNEL';

export type PlatformEventType =
  | 'ORDER_CREATED'
  | 'ORDER_FAILED'
  | 'ORDER_COMPLEMENTED'
  | 'USER_REGISTERED'
  | 'USER_LOGIN'
  | 'PRODUCT_VIEWED'
  | 'PRODUCT_ADDED_CART'
  | 'PRODUCT_REMOVED_CART'
  | 'CATEGORY_VIEWED';

export type UserLoginMethod =
  | 'LOGIN'
  | 'SUE'
  | 'PING'
  | 'EXTERNAL_CHANNEL_ACCESS';

export type PlatformEntityType =
  | 'ORDER'
  | 'PRODUCT'
  | 'USER'
  | 'OFFER'
  | 'CHECKOUT';

export interface OrderEventProduct {
  productName: string;
  variationName?: string;
  variationUuid?: string;
  quantity: number;
  amount: number;
}

export interface OrderCreatedAttributes {
  orderUuid: string;
  total: number;
  paymentMethod: string;
  status: string;
  products: OrderEventProduct[];
  sellerCode?: string;
}

export interface OrderFailedAttributes {
  orderType: 'SVA' | 'MIND' | 'SVA_AND_MIND';
  products: OrderEventProduct[];
  total?: number;
  paymentMethod?: string;
  status: 'FAILED';
  errorMessage?: string;
  errorCode?: string | number;
}

export interface OrderComplementedAttributes {
  token?: string;
  productUuid?: string;
  productName?: string;
  variationName?: string;
  email?: string;
  cnpj?: string;
  companyName?: string;
  originActivation: 'HUB';
  status: string;
}

export interface UserLoginAttributes {
  method: UserLoginMethod;
  userUuid?: string;
  login?: string;
  email?: string;
  campaign?: string;
}

export interface CartEventItem {
  productName: string | null;
  variationName: string | null;
  variationUuid: string | null;
  quantity: number;
  amount: number;
  isCartEmpty: boolean;
}

export interface ProductRemovedCartAttributes {
  products: CartEventItem[];
  isCartEmpty: boolean;
  remainingProducts?: CartEventItem[];
}

export interface PlatformEventRequest {
  originType: EventOriginType;
  slug: string;
  event: PlatformEventType;
  entityType: PlatformEntityType;
  description?: string;
  attributes?: string;
}

export interface PlatformEventResponse {
  uuid: string;
  originType: EventOriginType;
  slug: string;
  event: string;
  entityType: string;
  description?: string;
  attributes?: Record<string, unknown> | string;
  createdAt: string;
}

export interface ResolvedEventOrigin {
  originType: EventOriginType;
  slug: string;
}

const HUB_MARKETPLACE_SLUG = 'HUB_MARKETPLACE';

@Injectable({
  providedIn: 'root',
})
export class ChannelEventsService {
  private readonly API_URL = '/umanager/events';
  private readonly platformId = inject(PLATFORM_ID);
  private readonly apiService = inject(ApiService);
  private readonly utmPreservationService = inject(UtmPreservationService);
  private readonly router = inject(Router);

  private getUtmSourceSlug(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    const preservedParams = this.utmPreservationService.preservedUtmParams;
    const utmSource = (preservedParams['utm_source'] || '').trim();
    if (utmSource) {
      return utmSource;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const source = (urlParams.get('utm_source') || '').trim();
    return source || null;
  }

  resolveOrigin(): ResolvedEventOrigin {
    const utmSource = this.getUtmSourceSlug();
    if (utmSource) {
      return {
        originType: 'SALES_CHANNEL',
        slug: utmSource,
      };
    }

    if (isPlatformBrowser(this.platformId)) {
      const externalOrigin = readStoredExternalChannelOrigin();
      if (externalOrigin) {
        return {
          originType: 'ACCESS_CHANNEL',
          slug: externalOrigin,
        };
      }
    }

    return {
      originType: 'ACCESS_CHANNEL',
      slug: HUB_MARKETPLACE_SLUG,
    };
  }

  private removeUtmSourceFromUrl(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('utm_source');

      // Channel explicitly invalid -> remove and clear persisted UTM so it won't be re-applied.
      this.utmPreservationService.clearPreservedUtm();

      let newUrl = url.pathname;
      const remainingParams = new URLSearchParams(url.search);
      if (remainingParams.toString()) {
        newUrl += '?' + remainingParams.toString();
      }

      this.router.navigateByUrl(newUrl, { replaceUrl: true }).catch(err => {
        console.error('Error navigating after removing utm_source:', err);
      });
    } catch (error) {
      console.error('Error removing utm_source from URL:', error);
    }
  }

  sendEvent(
    event: PlatformEventType,
    entityType: PlatformEntityType,
    attributes?: Record<string, unknown>,
    description?: string
  ): Observable<PlatformEventResponse | null> {
    const { originType, slug } = this.resolveOrigin();

    const campaign = readStoredExternalChannelCampaign();
    const payloadAttributes =
      campaign && attributes ? { ...attributes, campaign } : attributes;

    const request: PlatformEventRequest = {
      originType,
      slug,
      event,
      entityType,
      ...(description ? { description } : {}),
      ...(payloadAttributes ? { attributes: JSON.stringify(payloadAttributes) } : {}),
    };

    return this.apiService.post<PlatformEventResponse>(this.API_URL, request).pipe(
      tap(() => {
        console.log(
          `Evento ${event} enviado com sucesso (${originType}/${slug})`
        );
      }),
      catchError(error => {
        if (
          originType === 'SALES_CHANNEL' &&
          error?.status === 400 &&
          error?.error?.message?.includes('Channel not registered')
        ) {
          console.warn(`Canal ${slug} não registrado. Removendo utm_source da URL.`);
          this.removeUtmSourceFromUrl();
        } else {
          console.error(`Erro ao enviar evento ${event} (${originType}/${slug}):`, error);
        }
        return of(null);
      })
    );
  }

  sendAddedCartEvent(
    items: CartEventItem[]
  ): Observable<PlatformEventResponse | null> {
    return this.sendEvent(
      'PRODUCT_ADDED_CART',
      'PRODUCT',
      { products: items },
      'Produto adicionado ao carrinho'
    );
  }

  sendProductViewedEvent(
    productName: string,
    extra?: { productUuid?: string; variationUuid?: string }
  ): Observable<PlatformEventResponse | null> {
    return this.sendEvent(
      'PRODUCT_VIEWED',
      'PRODUCT',
      {
        productName,
        ...(extra?.productUuid ? { productUuid: extra.productUuid } : {}),
        ...(extra?.variationUuid ? { variationUuid: extra.variationUuid } : {}),
      },
      'Produto visualizado'
    );
  }

  sendOrderCreatedEvent(
    attributes: OrderCreatedAttributes
  ): Observable<PlatformEventResponse | null> {
    return this.sendEvent('ORDER_CREATED', 'ORDER', { ...attributes }, 'Pedido criado');
  }

  sendUserRegisteredEvent(
    name: string,
    cnpj: string,
    email: string
  ): Observable<PlatformEventResponse | null> {
    return this.sendEvent(
      'USER_REGISTERED',
      'USER',
      { name, cnpj, email },
      'Usuário registrado'
    );
  }

  sendCategoryViewedEvent(category: string): Observable<PlatformEventResponse | null> {
    return this.sendEvent(
      'CATEGORY_VIEWED',
      'OFFER',
      { category },
      'Categoria visualizada'
    );
  }

  sendOrderComplementedEvent(
    attributes: OrderComplementedAttributes
  ): Observable<PlatformEventResponse | null> {
    return this.sendEvent(
      'ORDER_COMPLEMENTED',
      'ORDER',
      { ...attributes },
      'Pedido complementado'
    );
  }

  sendUserLoginEvent(
    attributes: UserLoginAttributes
  ): Observable<PlatformEventResponse | null> {
    return this.sendEvent(
      'USER_LOGIN',
      'USER',
      { ...attributes },
      'Usuário autenticado'
    );
  }

  sendOrderFailedEvent(
    attributes: OrderFailedAttributes
  ): Observable<PlatformEventResponse | null> {
    return this.sendEvent(
      'ORDER_FAILED',
      'ORDER',
      { ...attributes },
      'Falha ao criar pedido'
    );
  }

  sendProductRemovedCartEvent(
    attributes: ProductRemovedCartAttributes
  ): Observable<PlatformEventResponse | null> {
    return this.sendEvent(
      'PRODUCT_REMOVED_CART',
      'PRODUCT',
      { ...attributes },
      'Produto removido do carrinho'
    );
  }
}
